import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Container,
    FormControl,
    FormLabel,
    Input,
    Select,
    Stack,
    Heading,
    useToast,
    useColorModeValue,
    Spinner,
    Text,
    Flex,
} from '@chakra-ui/react';
import axios from 'axios';
import API_BASE from '../apiConfig';

function ProfilePage() {
    const toast = useToast();
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        age: '',
        gender: '',
        height: '',
        weight: ''
    });
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const bgColor = useColorModeValue('white', 'gray.800');

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await axios.get(`${API_BASE}/auth/me`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setFormData(res.data);
            } catch (err) {
                toast({
                    title: 'Failed to load profile',
                    status: 'error',
                    isClosable: true,
                });
            } finally {
                setLoading(false);
            }
        };
        fetchProfile(); 
    }, [toast]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.age <= 0 || !['male', 'female', 'other'].includes(formData.gender)) {
            toast({ title: 'Invalid input.', status: 'error', isClosable: true });
            return;
        }

        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API_BASE}/auth/me`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast({
                title: 'Profile update successfully',
                status: 'success',
                duration: 2000,
                isClosable: true,
            });
            setIsEditing(false);
        } catch (err) {
            toast({
                title: 'Failed to update profile.',
                status: 'error',
                isClosable: true,
            });
        }
    };

    if (loading) {
        return (
            <Container mawW="md" py={20} centerContent>
                <Spinner size="xl" color="green.500" />
            </Container>
        );
    }

    return (
        <Container maxW="md" py={10}>
            <Box bg={bgColor} p={8} borderWidth={1} borderRadius="lg" boxShadow="md">
                <Heading size="lg" mb={6} textAlign="center" color="green.600">
                    Your Profile
                </Heading>

                {isEditing ? (
                    <form onSubmit={handleSubmit}>
                        <Stack spacing={4}>
                            <FormControl isRequired>
                                <FormLabel>First Name</FormLabel>
                                <Input name="first_name" value={formData.first_name} onChange={handleChange} />
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel>Last Name</FormLabel>
                                <Input name="last_name" value={formData.last_name} onChange={handleChange} />
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel>Age</FormLabel>
                                <Input type="number" name="age" value={formData.age} onChange={handleChange} min={1} />
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel>Gender</FormLabel>
                                <Select name="gender" value={formData.gender} onChange={handleChange}>
                                    <option value="">Select Gender</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                    <option value="other">Other</option>
                                </Select>
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel>Height (cm)</FormLabel>
                                <Input type="number" name="height" value={formData.height} onChange={handleChange} min={1} />
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel>Weight (kg)</FormLabel>
                                <Input type="number" name="weight" value={formData.weight} onChange={handleChange} min={1} />
                            </FormControl>
                            <Flex justify="space-between" mt={4}>
                                <Button onClick={() => setIsEditing(false)} variant="outline">Cancel</Button>
                                <Button colorScheme="green" type="submit">Save</Button>
                            </Flex>
                        </Stack>
                    </form>
                ) : (
                    <Stack spacing={4}>
                        <Text><strong>First Name:</strong> {formData.first_name}</Text>
                        <Text><strong>Last Name:</strong> {formData.last_name}</Text>
                        <Text><strong>Age:</strong> {formData.age}</Text>
                        <Text><strong>Gender:</strong> {formData.gender}</Text>
                        <Text><strong>Height:</strong> {formData.height} cm</Text>
                        <Text><strong>Weight:</strong> {formData.weight} kg</Text>
                        <Button mt={4} onClick={() => setIsEditing(true)} colorScheme="green">Edit</Button>
                    </Stack>
                )}
            </Box>
        </Container>
    );
}

export default ProfilePage;